import React, { useState, useEffect, useRef } from 'react';
import {
  Zap,
  Lightbulb,
  TrendingUp,
  AlertCircle,
  Clock,
  Target,
  ArrowRight,
  CheckCircle,
  Brain,
  Sparkles,
  Calendar,
  Shield,
  Activity,
  BarChart3
} from 'lucide-react';

const InsightsDemo = ({ data }) => {
  const [activeInsight, setActiveInsight] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [insightActioned, setInsightActioned] = useState({});
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  const insights = [
    {
      id: 1,
      type: 'urgent',
      category: 'Deadline Alert',
      icon: AlertCircle,
      color: 'red',
      title: 'UC Application Closing Soon',
      message: 'UC Berkeley application closes in 15 days. You have 2 essays remaining.',
      impact: 'Missing this deadline eliminates 4 UC schools from your list',
      action: 'Complete UC essays now',
      timeEstimate: '4 hours',
      progress: 70
    },
    {
      id: 2,
      type: 'opportunity',
      category: 'Strategic Advantage',
      icon: Target,
      color: 'violet',
      title: 'MIT Interview Prep Opportunity',
      message: 'Your city planning experience is highly unusual for engineering applicants. Prepare specific stories.',
      impact: 'Interviews carry 15% weight in MIT admissions',
      action: 'Schedule mock interview',
      timeEstimate: '2 hours prep',
      progress: 0
    },
    {
      id: 3,
      type: 'optimization',
      category: 'Efficiency Boost',
      icon: Zap,
      color: 'yellow',
      title: 'Batch Similar Tasks',
      message: 'You have 5 short-answer questions across different applications with overlapping themes.',
      impact: 'Save 3 hours by writing them together',
      action: 'Start batch session',
      timeEstimate: '2 hours total',
      progress: 0
    },
    {
      id: 4,
      type: 'prevention',
      category: 'Risk Prevention',
      icon: Shield,
      color: 'azure',
      title: 'Recommender Follow-Up Needed',
      message: 'Dr. Martinez hasn\'t submitted to MIT yet. Deadline in 10 days.',
      impact: 'Missing recommendation = incomplete application',
      action: 'Send reminder email',
      timeEstimate: '5 minutes',
      progress: 0
    }
  ];

  const getIconColor = (color) => {
    switch(color) {
      case 'red': return 'text-red-500 bg-red-50 border-red-200';
      case 'violet': return 'text-violet-500 bg-violet-50 border-violet-200';
      case 'yellow': return 'text-yellow-500 bg-yellow-50 border-yellow-200';
      case 'azure': return 'text-azure-500 bg-azure-50 border-azure-200';
      default: return 'text-slate-500 bg-slate-50 border-slate-200';
    }
  };

  const handleAction = (insightId) => {
    setInsightActioned(prev => ({ ...prev, [insightId]: true }));
    setTimeout(() => {
      setInsightActioned(prev => ({ ...prev, [insightId]: false }));
    }, 2000);
  };

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-50 rounded-full mb-4">
          <Brain className="w-4 h-4 text-yellow-600" />
          <span className="text-sm font-medium text-yellow-700">
            Proactive Intelligence
          </span>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          AI-Powered Strategic Guidance
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Our system continuously analyzes your progress, deadlines, and opportunities
          to surface actionable insights that keep you ahead of the curve.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Insights Feed */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200">
            {/* Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-yellow-500" />
                  Real-Time Insights Feed
                </h3>
                <span className="text-sm text-slate-600">
                  Updated 2 minutes ago
                </span>
              </div>
            </div>

            {/* Insights List */}
            <div className="p-6 space-y-4">
              {insights.map((insight, index) => {
                const Icon = insight.icon;
                return (
                  <div
                    key={insight.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all
                      ${activeInsight === index
                        ? 'ring-2 ring-yellow-500 shadow-md'
                        : 'hover:shadow-md'}`}
                    onClick={() => setActiveInsight(index)}
                    style={{
                      animationDelay: `${index * 100}ms`,
                      animation: isInView ? 'fadeInSlide 0.6s ease-out forwards' : 'none'
                    }}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg border ${getIconColor(insight.color)}`}>
                        <Icon className="w-5 h-5" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded
                              ${insight.type === 'urgent' ? 'bg-red-100 text-red-700' :
                                insight.type === 'opportunity' ? 'bg-violet-100 text-violet-700' :
                                insight.type === 'optimization' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-azure-100 text-azure-700'}`}>
                              {insight.category}
                            </span>
                            <h4 className="font-semibold text-slate-900 mt-2">{insight.title}</h4>
                          </div>
                          {insight.progress > 0 && (
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-emerald-500"
                                  style={{ width: `${insight.progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-slate-600">{insight.progress}%</span>
                            </div>
                          )}
                        </div>

                        <p className="text-sm text-slate-600 mb-3">
                          {insight.message}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {insight.timeEstimate}
                            </span>
                            <span className="text-xs font-medium text-orange-600">
                              Impact: {insight.impact}
                            </span>
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction(insight.id);
                            }}
                            className="px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-sm font-medium rounded-lg hover:shadow-md transition-all flex items-center gap-1"
                          >
                            {insightActioned[insight.id] ? (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                Started!
                              </>
                            ) : (
                              <>
                                {insight.action}
                                <ArrowRight className="w-3 h-3" />
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: Insights Dashboard */}
        <div className="space-y-4">
          {/* Insight Categories */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-slate-900">Insight Categories</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full" />
                  <span className="text-sm text-slate-700">Urgent Actions</span>
                </div>
                <span className="text-sm font-bold text-slate-900">2</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-violet-500 rounded-full" />
                  <span className="text-sm text-slate-700">Opportunities</span>
                </div>
                <span className="text-sm font-bold text-slate-900">3</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                  <span className="text-sm text-slate-700">Optimizations</span>
                </div>
                <span className="text-sm font-bold text-slate-900">5</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-azure-500 rounded-full" />
                  <span className="text-sm text-slate-700">Preventions</span>
                </div>
                <span className="text-sm font-bold text-slate-900">1</span>
              </div>
            </div>
          </div>

          {/* Time Savings */}
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border border-yellow-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-yellow-600" />
              <h3 className="font-semibold text-slate-900">Impact This Week</h3>
            </div>

            <div className="space-y-3">
              <div className="text-center p-3 bg-white rounded-lg border border-yellow-200">
                <div className="text-2xl font-bold text-yellow-600">12 hours</div>
                <div className="text-xs text-slate-600">saved through optimizations</div>
              </div>

              <div className="text-center p-3 bg-white rounded-lg border border-orange-200">
                <div className="text-2xl font-bold text-orange-600">3 risks</div>
                <div className="text-xs text-slate-600">prevented before issues</div>
              </div>

              <div className="text-center p-3 bg-white rounded-lg border border-emerald-200">
                <div className="text-2xl font-bold text-emerald-600">100%</div>
                <div className="text-xs text-slate-600">deadlines met</div>
              </div>
            </div>
          </div>

          {/* Next Best Action */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-azure-600" />
              <h3 className="font-semibold text-slate-900">Recommended Focus</h3>
            </div>

            <div className="p-4 bg-azure-50 rounded-lg border border-azure-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-azure-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  1
                </div>
                <div>
                  <div className="font-medium text-slate-900 mb-1">
                    Complete UC Essays Today
                  </div>
                  <div className="text-xs text-slate-600 mb-2">
                    This unlocks review time and reduces last-minute stress
                  </div>
                  <button className="text-xs font-medium text-azure-600 hover:text-azure-700 flex items-center gap-1">
                    Start focused session
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default InsightsDemo;